export const BUCKET_INTERVALS = ["hour", "day", "week", "month"] as const;

export type BucketInterval = (typeof BUCKET_INTERVALS)[number];

export const MAX_RANGE_BUCKETS = 1_000;

const BUCKET_MS: Readonly<Record<BucketInterval, number>> = {
  hour: 3_600_000,
  day: 86_400_000,
  week: 604_800_000,
  month: 2_592_000_000,
};

const DAY_MS = BUCKET_MS.day;

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const INSTANT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?Z$/;

export type RangeEdge = "since" | "until";

export const isDayBound = (value: string): boolean => DAY_PATTERN.test(value);

const isInstantBound = (value: string): boolean => INSTANT_PATTERN.test(value);

export const isCalendarDay = (day: string): boolean => {
  const midnight = Date.parse(`${day}T00:00:00Z`);
  return !Number.isNaN(midnight) && new Date(midnight).toISOString().startsWith(day);
};

export const isRangeBound = (value: string): boolean =>
  isDayBound(value)
    ? isCalendarDay(value)
    : isInstantBound(value) &&
      isCalendarDay(value.slice(0, 10)) &&
      !Number.isNaN(Date.parse(value));

export const boundInstantMs = (value: string, edge: RangeEdge): number => {
  if (isDayBound(value)) {
    const midnight = Date.parse(`${value}T00:00:00Z`);
    return edge === "until" ? midnight + DAY_MS : midnight;
  }
  return Date.parse(value);
};

export const bucketsInRange = (
  since: string,
  until: string,
  interval: BucketInterval = "day",
): number => {
  const from = boundInstantMs(since, "since");
  const to = boundInstantMs(until, "until");
  if (Number.isNaN(from) || Number.isNaN(to) || to < from) return 0;
  return Math.ceil((to - from) / BUCKET_MS[interval]);
};

export const rangeFitsBudget = (
  since: string | undefined,
  until: string | undefined,
  interval: BucketInterval | undefined,
): boolean =>
  since === undefined ||
  until === undefined ||
  bucketsInRange(since, until, interval) <= MAX_RANGE_BUCKETS;
